import React, { useState } from "react";
import { api } from "../api";
import { dateShort } from "./ui";

/**
 * The LinkedIn audit we ran for this company: the flag and what it found.
 *
 * A HAND FLAG (companies.audited_at, migration 043). A prospect's audit usually
 * happens outside the auditor — they give us viewer access and we look — so the
 * auditor's own last_audited_at almost never covers one. This is Gabriel saying
 * "we audited them", dated the moment he ticks it.
 *
 * ONE component for both places it is edited, the record's rail and the board
 * card's ⋯ menu, for the same reason AssociatedContacts is one: two copies of a
 * form are two places for it to drift.
 *
 * `compact` is the board popover: the editor straight away, no card around it.
 */

/** The tag's hover: when, and the start of what it found. */
export function auditTitle(company) {
  if (!company || !company.audited_at) return "";
  const notes = (company.audit_notes || "").trim();
  const head = `LinkedIn audit · ${dateShort(company.audited_at)}`;
  return notes ? `${head}\n${notes.length > 240 ? `${notes.slice(0, 240)}…` : notes}` : head;
}

export default function AuditNotes({ company, onChanged, compact = false, onDone = null }) {
  const [editing, setEditing] = useState(compact);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!company) return null;
  const audited = !!company.audited_at;
  const notes = (company.audit_notes || "").trim();
  const current = form || { audited, notes: company.audit_notes || "" };

  function open() {
    setForm({ audited, notes: company.audit_notes || "" });
    setError(null);
    setEditing(true);
  }

  function close() {
    setForm(null);
    setEditing(false);
    if (onDone) onDone();
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api(`/companies/${company.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          // Keep the original date when it stays ticked: the date is when the
          // audit was done, not when the notes were last touched.
          audited_at: current.audited ? (company.audited_at || new Date().toISOString()) : null,
          audit_notes: current.notes.trim() || null,
        }),
      });
      setForm(null);
      setEditing(false);
      if (onChanged) await onChanged();
      if (onDone) onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const tag = audited && (
    <span className="crm-tag crm-tag--audited">audited {dateShort(company.audited_at)}</span>
  );

  if (editing) {
    const body = (
      <>
        <label className="crm-audit__check">
          <input
            type="checkbox"
            checked={current.audited}
            disabled={busy}
            onChange={(e) => setForm({ ...current, audited: e.target.checked })}
          />
          <span>We audited their LinkedIn Ads account</span>
        </label>
        <textarea
          className="crm-terms__area crm-audit__area"
          rows={compact ? 5 : 7}
          placeholder="What the audit found: structure, targeting, spend, the pitch it sets up"
          value={current.notes}
          disabled={busy}
          onChange={(e) => setForm({ ...current, notes: e.target.value })}
        />
        {error && <span className="crm-terms__err">{error}</span>}
        <div className="crm-terms__actions">
          <button type="button" className="crm-btn crm-btn--primary" disabled={busy} onClick={save}>
            Save
          </button>
          <button type="button" className="crm-linkact" disabled={busy} onClick={close}>
            cancel
          </button>
        </div>
      </>
    );
    if (compact) return <div className="crm-audit crm-audit--compact">{body}</div>;
    return (
      <div className="crm-card crm-cd__railcard crm-terms crm-audit">
        <div className="crm-terms__head">
          <span className="crm-cd__raillabel">LinkedIn audit</span>
          {tag}
        </div>
        {body}
      </div>
    );
  }

  return (
    <div className="crm-card crm-cd__railcard crm-terms crm-audit">
      <div className="crm-terms__head">
        <span className="crm-cd__raillabel">LinkedIn audit</span>
        {tag}
      </div>
      {notes ? (
        <p className="crm-audit__notes">{notes}</p>
      ) : (
        <span className="crm-terms__hint">
          {audited ? "No notes yet." : "Gave us viewer access? Mark it once the audit is done."}
        </span>
      )}
      <div className="crm-terms__actions">
        <button type="button" className="crm-btn crm-btn--secondary" onClick={open}>
          {audited || notes ? "Edit" : "Mark audited"}
        </button>
      </div>
    </div>
  );
}
